import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Monitor, KeyRound, CheckCircle2, ArrowLeft } from 'lucide-react'
import { attendanceApi } from '@/api/attendance'
import { deviceApi, setStoredDeviceToken, getStoredDeviceToken, getDeviceInfo } from '@/api/device'
import { useAuthStore } from '@/store/authStore'
import { useAttendanceWatchStore } from '@/store/attendanceWatchStore'
import { useDeviceStore } from '@/store/deviceStore'
import { useTenantStore } from '@/store/tenantStore'
import { canAccessLearner } from '@/utils/roleUtils'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'

interface ActivationResult {
  deviceName: string
  checkedIn: boolean
}

export default function DeviceActivatePage() {
  const { t } = useTranslation('learner')
  const { tenantInfo } = useTenantStore()
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)
  const centerName = tenantInfo?.displayName || tenantInfo?.name || '러닝센터'

  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCheckInRetrying, setIsCheckInRetrying] = useState(false)
  const [checkInError, setCheckInError] = useState(false)
  const [result, setResult] = useState<ActivationResult | null>(null)

  // 이미 등록된 기기인지 확인
  const existingToken = getStoredDeviceToken()

  // URL 파라미터에서 코드 자동 입력 (QR 스캔 시)
  const codeFromUrl = searchParams.get('code')
  useEffect(() => {
    if (codeFromUrl && codeFromUrl.length === 8) {
      setCode(codeFromUrl.toUpperCase())
    }
  }, [codeFromUrl])

  const isAuthenticatedLearner =
    isAuthenticated && user != null && canAccessLearner(user.role)

  const checkInCurrentLearner = async (): Promise<boolean> => {
    if (!isAuthenticatedLearner) return false

    const response = await attendanceApi.checkInRegisteredDevice()
    const deviceContext = response.data.data
    useDeviceStore.getState().setDeviceContext(deviceContext)
    useAttendanceWatchStore
      .getState()
      .resetForLogin(user.id, deviceContext.attendanceUpdated)

    // 기기등록 전의 미등원/OUT 캐시가 오늘수업 게이트나 watcher를 다시 막지 않게 한다.
    queryClient.removeQueries({ queryKey: ['learner', 'attendance', 'today'] })
    queryClient.removeQueries({
      queryKey: ['learner', 'me', 'attendance-watch', user.id],
    })
    return deviceContext.attendanceUpdated
  }

  const retryCheckIn = async () => {
    if (!result) return
    setIsCheckInRetrying(true)
    setCheckInError(false)
    try {
      const checkedIn = await checkInCurrentLearner()
      setResult({ ...result, checkedIn })
    } catch {
      setCheckInError(true)
    } finally {
      setIsCheckInRetrying(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      const response = await deviceApi.activate(code, getDeviceInfo())
      const data = response.data.data
      setStoredDeviceToken(data.deviceToken)
      useDeviceStore.getState().setDeviceContext({
        isRegistered: true,
        deviceId: null,
        attendanceUpdated: false,
      })

      let checkedIn = false
      if (isAuthenticatedLearner) {
        try {
          checkedIn = await checkInCurrentLearner()
        } catch {
          setCheckInError(true)
        }
      }
      setResult({ deviceName: data.deviceName, checkedIn })
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || '등록 코드가 유효하지 않습니다.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-lc-gray-c100 flex flex-col">
      {/* Header */}
      <header
        className="h-14 flex items-center px-6 shadow-lc-chip"
        style={{ backgroundColor: tenantInfo?.primaryColor || '#6366f1' }}
      >
        <div className="flex items-center space-x-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantInfo?.name || 'Quebon'}
              className="h-7 w-auto"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/quebon-logo.svg'
              }}
            />
          ) : (
            <div className="h-7 w-7 rounded bg-white/20 flex items-center justify-center text-white font-bold text-lc-caption-1">
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <span className="font-bold text-white text-lc-body-2">{centerName}</span>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {result ? (
            // 등록 완료
            <div className="bg-white rounded-lc-xl shadow-lc-soft p-8 text-center">
              <div className="size-16 bg-lc-mint-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="size-8 text-lc-mint-dark" />
              </div>
              <h1 className="text-lc-headline-3 text-lc-gray-900 mb-2">
                기기 등록 완료
              </h1>
              <p className="text-lc-body-3 text-lc-gray-600 mb-2">
                <span className="font-medium text-lc-gray-700">"{result.deviceName}"</span>(으)로 등록되었습니다.
              </p>
              <p className="text-lc-caption-1 text-lc-gray-500 mb-6">
                이제 이 기기에서 수업에 접근할 수 있습니다.
              </p>
              {checkInError ? (
                <>
                  <p className="mb-4 text-lc-caption-1 text-lc-red-dark">
                    {t('device.activation.checkInFailed')}
                  </p>
                  <button
                    onClick={() => void retryCheckIn()}
                    disabled={isCheckInRetrying}
                    className="w-full px-4 py-3 bg-lc-orange-900 text-white text-lc-body-3 font-bold rounded-lc-lg shadow-lc-3d-orange active:translate-y-[2px] active:shadow-lc-3d-orange-active disabled:opacity-50"
                  >
                    {isCheckInRetrying
                      ? t('device.activation.checkInRetrying')
                      : t('device.activation.retryCheckIn')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() =>
                    navigate(
                      result.checkedIn
                        ? '/my/today'
                        : isAuthenticatedLearner
                          ? '/my'
                          : '/login',
                    )
                  }
                  className="w-full px-4 py-3 bg-lc-orange-900 text-white text-lc-body-3 font-bold rounded-lc-lg shadow-lc-3d-orange active:translate-y-[2px] active:shadow-lc-3d-orange-active"
                >
                  {result.checkedIn
                    ? t('device.activation.goToClass')
                    : isAuthenticatedLearner
                      ? t('device.activation.goToLearning')
                      : t('device.activation.goToLogin')}
                </button>
              )}
            </div>
          ) : (
            // 등록 폼
            <div className="bg-white rounded-lc-xl shadow-lc-soft p-8">
              <div className="text-center mb-6">
                <div className="size-16 bg-lc-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Monitor className="size-8 text-lc-orange-dark" />
                </div>
                <h1 className="text-lc-headline-3 text-lc-gray-900">기기 등록</h1>
                <p className="text-lc-body-3 text-lc-gray-600 mt-2">
                  관리자에게 받은 등록 코드를 입력해 주세요.
                </p>
              </div>

              {existingToken && (
                <div className="mb-4 p-3 bg-lc-lightblue-100 border border-lc-lightblue-300 rounded-lc-md">
                  <p className="text-lc-caption-1 text-lc-lightblue-dark">
                    이 기기는 이미 등록되어 있습니다. 새 코드를 입력하면 재등록됩니다.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-lc-caption-1 font-semibold text-lc-gray-800 mb-1">
                    등록 코드
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-lc-gray-500" />
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      className="w-full h-12 pl-10 pr-4 border border-lc-gray-200 rounded-lc-md focus:ring-2 focus:ring-lc-lightblue-100 focus:border-lc-lightblue-900 outline-none font-mono text-center text-lc-body-2 tracking-widest"
                      placeholder="ABCD1234"
                      maxLength={8}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-lc-caption-1 text-lc-red-dark">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || code.length !== 8}
                  className="w-full px-4 py-3 bg-lc-orange-900 text-white text-lc-body-3 font-bold rounded-lc-lg shadow-lc-3d-orange active:translate-y-[2px] active:shadow-lc-3d-orange-active disabled:opacity-50 disabled:active:translate-y-0"
                >
                  {isSubmitting ? '확인 중...' : '기기 등록'}
                </button>
              </form>

              <button
                onClick={() => navigate('/login')}
                className="w-full flex items-center justify-center space-x-2 mt-4 px-4 py-2 text-lc-gray-500 hover:text-lc-gray-700 text-lc-caption-1"
              >
                <ArrowLeft className="size-4" />
                <span>로그인 페이지로 돌아가기</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
